import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  Gamepad2,
  Zap as Lightning,
  Target,
  Gift,
  Users,
  UserCheck,
  Settings,
  X,
  Book,
  Brain,
  BarChart3,
  Headphones,
  Sparkles
} from 'lucide-react';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { getUnreadTicketCount, getUnreadTicketCountByTenant } from '../services/supportTicketApi';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const formatRole = (role: string): string => {
  return role
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const Sidebar = ({ isOpen, onClose }: SidebarProps) => {
  const location = useLocation();
  const { tenant } = useTenant();
  const { user } = useAuth();
  const { t } = useTranslation();
  const { isImpersonating, impersonatedTenant } = useImpersonation();
  const [unreadTicketCount, setUnreadTicketCount] = useState(0);

  useEffect(() => {
    if (isImpersonating && impersonatedTenant?.id) {
      loadUnreadCountByTenant();
      const interval = setInterval(loadUnreadCountByTenant, 60000);
      return () => clearInterval(interval);
    } else if (user?.id) {
      loadUnreadCount();
      const interval = setInterval(loadUnreadCount, 60000);
      return () => clearInterval(interval);
    }
  }, [user?.id, isImpersonating, impersonatedTenant?.id]);

  const loadUnreadCount = async () => {
    if (!user?.id) return;
    try {
      const count = await getUnreadTicketCount(user.id);
      setUnreadTicketCount(count);
    } catch (error) {
      console.error('Failed to load unread ticket count:', error);
    }
  };

  const loadUnreadCountByTenant = async () => {
    if (!impersonatedTenant?.id) return;
    try {
      const count = await getUnreadTicketCountByTenant(impersonatedTenant.id);
      setUnreadTicketCount(count);
    } catch (error) {
      console.error('Failed to load tenant unread ticket count:', error);
    }
  };

  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (isOpen && !target.closest('.sidebar-container')) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const isAdmin = user?.role === 'admin';

  const navigationSections = useMemo(() => {
    const sections = [
      {
        label: null,
        items: [
          { name: t('dashboard'), href: '/dashboard', icon: LayoutDashboard }
        ]
      },
      {
        label: t('sidebar.gamification_center', 'Gamification Center'),
        items: [
          { name: t('game_contest', 'Game Contest'), href: '/html5-games', icon: Gamepad2 },
          { name: t('instant_win'), href: '/instant-win', icon: Lightning },
          { name: t('challenges'), href: '/challenges', icon: Target },
          { name: t('trivia_quizzes'), href: '/trivia-quizzes', icon: Brain },
          { name: t('rewards'), href: '/rewards', icon: Gift }
        ]
      },
      {
        label: t('sidebar.usage', 'Usage'),
        items: [
          { name: t('participants'), href: '/participants', icon: UserCheck },
          { name: t('reports_analytics'), href: '/reports', icon: BarChart3 }
        ]
      }
    ];

    if (isAdmin) {
      sections.push({
        label: t('sidebar.administration', 'Administration'),
        items: [
          { name: t('api_documentation'), href: '/api-docs', icon: Book },
          { name: t('support.title', 'Support'), href: '/support', icon: Headphones, badge: unreadTicketCount },
          { name: t('settings'), href: '/settings', icon: Settings },
          { name: t('users'), href: '/users', icon: Users }
        ]
      });
    }

    return sections;
  }, [t, unreadTicketCount, isAdmin]);

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden" onClick={onClose} />
      )}

      {/* Sidebar */}
      <div className={`sidebar-container fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gray-800 shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      } flex flex-col`}>
        {/* Mobile close button */}
        <div className="lg:hidden flex justify-end p-4">
          <button
            onClick={onClose}
            className="p-2 rounded-md text-gray-400 dark:text-gray-300 hover:text-gray-600 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        
        {/* Logo */}
      <div className="flex items-center justify-center h-24 px-4 bg-white dark:bg-gray-900">
        <div className="flex items-center space-x-3">
          <div className="h-12 w-12 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg">
            <Sparkles className="h-7 w-7 text-white" />
          </div>
          <span className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-blue-800 to-cyan-400 bg-clip-text text-transparent">RewardPulse</span>
        </div>
      </div>
      
        {/* Tenant info */}
      {tenant && (
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{tenant.name}</p>
        </div>
      )}

        {/* Navigation */}
      <nav className="flex-1 px-4 py-4 overflow-y-auto">
        {navigationSections.map((section, sectionIndex) => (
          <div
            key={section.label || 'dashboard'}
            className={sectionIndex > 0 ? 'mt-4 pt-4 border-t border-gray-200 dark:border-gray-700' : ''}
          >
            {section.label && (
              <p className="px-3 mb-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {section.label}
              </p>
            )}
            <div className="space-y-1">
              {section.items.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={() => {
                      if (window.innerWidth < 1024) {
                        onClose();
                      }
                    }}
                    className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                      isActive
                        ? 'bg-blue-50 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 border-r-2 border-blue-700 dark:border-blue-500'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'
                    }`}
                  >
                    <item.icon className={`mr-3 h-5 w-5 ${isActive ? 'text-blue-700 dark:text-blue-300' : 'text-gray-400 dark:text-gray-500'}`} />
                    <span className="flex-1">{item.name}</span>
                    {item.badge !== undefined && item.badge > 0 && (
                      <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-blue-600 text-white rounded-full">
                        {item.badge}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

        {/* User info */}
      <div className="px-4 py-4 border-t dark:border-gray-700">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-teal-500 rounded-full flex items-center justify-center">
            <span className="text-white text-sm font-medium">
              {user?.name?.charAt(0).toUpperCase() || 'U'}
            </span>
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
              {user?.name || 'User'}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {user?.role ? formatRole(user.role) : 'User'}
            </p>
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default Sidebar;